import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getSaleById, convertToInvoice } from '../../services/api';

const ViewSale = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  
  const [sale, setSale] = useState(null);

  // Define status color mappings
  const statusColors = {
    'Draft': 'default',
    'Confirmed': 'info',
    'Processing': 'warning',
    'Shipped': 'primary',
    'Delivered': 'success',
    'Cancelled': 'error'
  };

  const paymentStatusColors = {
    'Pending': 'warning',
    'Partial': 'info',
    'Paid': 'success'
  };

  console.log('🎯 Component rendered with ID:', id);

  // Define fetchSale with useCallback to avoid dependency issues
  const fetchSale = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🆔 fetchSale called with ID:', id);
      
      const response = await getSaleById(id);
      console.log('📦 API Response:', response);
      
      // Since interceptor returns response.data, response is already the data object
      if (response?.success) {
        console.log('✅ Setting sale data:', response.data);
        
        // Debug: Check serial numbers in items
        if (response.data.items) {
          console.log('🔍 Items count:', response.data.items.length);
          response.data.items.forEach((item, index) => {
            console.log(`🔍 Item ${index} serial_numbers:`, item.serial_numbers);
            console.log(`🔍 Item ${index} serial_numbers type:`, typeof item.serial_numbers);
            console.log(`🔍 Item ${index} serial_numbers isArray:`, Array.isArray(item.serial_numbers));
          });
        }
        
        setSale(response.data);
        setError('');
      } else {
        console.log('❌ API error:', response?.message);
        setError(response?.message || 'Failed to fetch sale details');
        setSale(null);
      }
    } catch (err) {
      console.error('❌ fetchSale error:', err);
      
      // Error is already processed by interceptor
      if (err.success === false) {
        setError(err.message || 'Failed to fetch sale details');
      } else {
        setError(err.message || 'Failed to fetch sale');
      }
      setSale(null);
    } finally {
      console.log('🏁 fetchSale completed');
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    console.log('🔧 useEffect triggered with ID:', id);
    fetchSale();
  }, [id, fetchSale]);

  const handleConvertToInvoice = async () => {
    try {
      setLoading(true);
      const response = await convertToInvoice(id);
      if (response.data.success) {
        setSuccess('Sale converted to invoice successfully!');
        setConvertDialogOpen(false);
        
        // Navigate to the new invoice
        setTimeout(() => {
          navigate(`/invoices/edit/${response.data.data.id}`);
        }, 1500);
      } else {
        setError(response.data.message || 'Failed to convert to invoice');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to convert to invoice');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00' || dateString === '0000-00-00T00:00:00.000Z') return 'N/A';
    return new Date(dateString).toLocaleDateString('en-LK', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const calculateItemTotal = (item) => {
    const subtotal = (item.quantity || 0) * (item.unit_price || 0);
    const discount = subtotal * ((item.discount_percentage || 0) / 100);
    return subtotal - discount;
  };

  const calculateTotals = () => {
    if (!sale || !sale.items) return { subtotal: 0, tax_amount: 0, total: 0 };
    
    const subtotal = sale.items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
    const tax_amount = subtotal * ((sale.tax_rate || 0) / 100);
    const total = subtotal + tax_amount - (parseFloat(sale.discount) || 0);
    
    return {
      subtotal: subtotal.toFixed(2),
      tax_amount: tax_amount.toFixed(2),
      total: total.toFixed(2)
    };
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Implement PDF download
    console.log('Download PDF');
  };

  const handleSendEmail = () => {
    // Implement email sending
    console.log('Send email');
  };

  // Helper function to safely handle serial numbers
  const getSerialNumbersArray = (serialNumbers) => {
    if (!serialNumbers) return [];
    if (Array.isArray(serialNumbers)) return serialNumbers;
    if (typeof serialNumbers === 'string') {
      try {
        // Try to parse JSON string
        const parsed = JSON.parse(serialNumbers);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        // If parsing fails, treat as single value
        return [serialNumbers];
      }
    }
    // For any other type, wrap in array
    return [serialNumbers];
  };

  // Debug: Log current state
  console.log('🔍 Current state:', { loading, sale, error });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading sale #{id}...
        </Typography>
      </Box>
    );
  }

  if (error && !sale) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/sales')}
          sx={{ mb: 3 }}
        >
          Back to Sales
        </Button>
        <Alert severity="error">
          {error}
          <Box mt={1}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={fetchSale}
              sx={{ mt: 1 }}
            >
              Retry
            </Button>
          </Box>
        </Alert>
      </Container>
    );
  }

  if (!sale) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/sales')}
          sx={{ mb: 3 }}
        >
          Back to Sales
        </Button>
        <Alert severity="warning">
          Sale #{id} not found or data is incomplete.
          <Box mt={1}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={fetchSale}
              sx={{ mt: 1 }}
            >
              Retry Loading
            </Button>
          </Box>
        </Alert>
      </Container>
    );
  }

  const totals = calculateTotals();
  
  // Debug: Check if items are present
  console.log('🔍 Sale data:', sale);
  console.log('🔍 Sale items:', sale.items);
  console.log('🔍 Sale items length:', sale.items?.length);
  console.log('🔍 Customer data:', sale.Customer);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/sales')}
        sx={{ mb: 3 }}
      >
        Back to Sales
      </Button>

      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Sales Order #{sale.order_number || sale.id}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Created: {formatDate(sale.created_at)}
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/sales/edit/${id}`)}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              startIcon={<ReceiptIcon />}
              onClick={() => setConvertDialogOpen(true)}
              disabled={sale.status === 'cancelled' || sale.status === 'Cancelled'}
            >
              Convert to Invoice
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Debug info - hidden but in DOM for inspection */}
        <Box sx={{ display: 'none' }}>
          <div id="debug-sale-data">{JSON.stringify(sale, null, 2)}</div>
          <div id="debug-sale-id">{sale.id}</div>
          <div id="debug-has-items">{!!sale.items}</div>
          <div id="debug-items-length">{sale.items?.length}</div>
          {sale.items?.map((item, idx) => (
            <div key={idx}>
              Item {idx} serial_numbers: {JSON.stringify(item.serial_numbers)}
            </div>
          ))}
        </Box>

        <Grid container spacing={3}>
          {/* Customer and Status */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Customer Name</Typography>
                    <Typography variant="body1">
                      {sale.Customer?.name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Email</Typography>
                    <Typography variant="body1">
                      {sale.Customer?.email || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Phone</Typography>
                    <Typography variant="body1">
                      {sale.Customer?.phone || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Address</Typography>
                    <Typography variant="body1">
                      {sale.Customer?.address || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Order Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">Order Date</Typography>
                    <Typography variant="body1">
                      {formatDate(sale.order_date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">Delivery Date</Typography>
                    <Typography variant="body1">
                      {formatDate(sale.delivery_date) || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">Status</Typography>
                    <Chip
                      label={sale.status}
                      color={statusColors[sale.status] || 'default'}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">Payment Status</Typography>
                    <Chip
                      label={sale.payment_status}
                      color={paymentStatusColors[sale.payment_status] || 'default'}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">Payment Method</Typography>
                    <Typography variant="body1">
                      {sale.payment_method ? sale.payment_method.charAt(0).toUpperCase() + sale.payment_method.slice(1) : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">Credit Period</Typography>
                    <Typography variant="body1">
                      {sale.credit_period ? `${sale.credit_period} days` : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Items Table */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Items {sale.items && `(${sale.items.length})`}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Disc %</TableCell>
                    <TableCell align="right">Warranty</TableCell>
                    <TableCell>Serial Numbers</TableCell>
                    <TableCell align="right">Line Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sale.items && sale.items.length > 0 ? (
                    sale.items.map((item, index) => {
                      // Safely get serial numbers as array
                      const serialNumbers = getSerialNumbersArray(item.serial_numbers);
                      
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {item.product_name || 'Unnamed Product'}
                            </Typography>
                            {item.product_id && (
                              <Typography variant="caption" color="textSecondary">
                                ID: {item.product_id}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{item.quantity || 0}</TableCell>
                          <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell align="right">{item.discount_percentage || 0}%</TableCell>
                          <TableCell align="right">{item.warranty_months || sale.warranty_period || 0} months</TableCell>
                          <TableCell>
                            <Box sx={{ maxWidth: 200 }}>
                              {serialNumbers.length > 0 ? (
                                serialNumbers.map((serial, serialIndex) => (
                                  <Typography key={serialIndex} variant="caption" display="block">
                                    {serial || `Serial ${serialIndex + 1}`}
                                  </Typography>
                                ))
                              ) : (
                                <Typography variant="caption" color="textSecondary">
                                  No serial numbers
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(calculateItemTotal(item))}</TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="textSecondary" py={2}>
                          No items found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Warranty Information */}
          {sale.warranty_period && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Warranty Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2">Warranty Period</Typography>
                      <Typography variant="body1">
                        {sale.warranty_period ? `${sale.warranty_period} months` : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2">Warranty Terms</Typography>
                      <Typography variant="body1">
                        {sale.warranty_terms || 'Standard manufacturer warranty'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Totals */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Order Summary
                </Typography>
                
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Subtotal:</Typography>
                  <Typography>{formatCurrency(totals.subtotal)}</Typography>
                </Box>
                
                {sale.tax_rate > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>VAT ({sale.tax_rate || 0}%):</Typography>
                    <Typography>{formatCurrency(totals.tax_amount)}</Typography>
                  </Box>
                )}
                
                {sale.discount > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Discount:</Typography>
                    <Typography>{formatCurrency(sale.discount)}</Typography>
                  </Box>
                )}
                
                <Divider sx={{ my: 2 }} />
                
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(totals.total)}
                  </Typography>
                </Box>
                
                {/* Show API totals for comparison */}
                <Box mt={2} pt={2} borderTop={1} borderColor="divider">
                  <Typography variant="caption" color="textSecondary">
                    API Totals (for reference):
                  </Typography>
                  <Box display="flex" justifyContent="space-between" fontSize="0.75rem">
                    <Typography variant="caption">Subtotal: {formatCurrency(sale.subtotal)}</Typography>
                    <Typography variant="caption">VAT: {formatCurrency(sale.tax_amount)}</Typography>
                    <Typography variant="caption">Total: {formatCurrency(sale.total_amount)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Notes */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  {sale.notes || 'No notes provided'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                >
                  Print
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                >
                  Download PDF
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={handleSendEmail}
                >
                  Send Email
                </Button>
              </Box>
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/sales/edit/${id}`)}
                >
                  Edit Order
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate(`/sales/edit/${id}`)}
                >
                  Update Status
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Convert to Invoice Dialog */}
      <Dialog open={convertDialogOpen} onClose={() => setConvertDialogOpen(false)}>
        <DialogTitle>Convert to Invoice</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to convert this sales order to an invoice?
            This will create a new invoice with all the items from this sale.
          </Typography>
          <Box mt={2}>
            <Alert severity="info">
              The invoice will be created with the customer details and items from this sale.
              You can make additional changes in the invoice form.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConvertToInvoice}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Convert'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ViewSale;